import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations, contactInfo } from '../data/mock';
import { Phone, MapPin, Clock, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { t } = useLanguage();
  const contactText = t(translations.contact);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);
    toast({
      title: 'Message Sent!',
      description: 'We will contact you soon.',
    });
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <section className="bg-gradient-to-r from-[#0B1F3F] to-[#1a3a5f] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{contactText.title}</h1>
          <p className="text-xl text-gray-200">{contactText.subtitle}</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-[#0B1F3F] mb-8">Get in Touch</h2>
              
              {/* Contact Cards */}
              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4 p-6 bg-white rounded-lg shadow-md border border-gray-100">
                  <div className="w-12 h-12 bg-[#FF6B35] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0B1F3F] mb-2">{contactText.phone}</h3>
                    <a href={`tel:${contactInfo.phone}`} className="text-gray-700 hover:text-[#FF6B35] transition-colors">
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-white rounded-lg shadow-md border border-gray-100">
                  <div className="w-12 h-12 bg-[#FF6B35] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0B1F3F] mb-2">{contactText.address}</h3>
                    <p className="text-gray-700">{contactText.addressText}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-white rounded-lg shadow-md border border-gray-100">
                  <div className="w-12 h-12 bg-[#FF6B35] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0B1F3F] mb-2">{contactText.hours}</h3>
                    <p className="text-gray-700">{contactText.hoursText}</p>
                  </div>
                </div>
              </div>

              {/* Quick Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white"
                >
                  <a href={`tel:${contactInfo.phone}`}>
                    <Phone className="h-4 w-4 mr-2" />
                    {contactText.call}
                  </a>
                </Button>
                <Button
                  asChild
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                >
                  <a href={contactInfo.whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    {contactText.whatsapp}
                  </a>
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white p-8 rounded-lg shadow-xl border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0B1F3F] mb-6">{contactText.formTitle}</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {contactText.name}
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {contactText.email}
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {contactText.message}
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#FF6B35] hover:bg-[#FF5722] text-white">
                    {contactText.submit}
                  </Button>
                </form>
              </div>
            </div>
          </div>

          {/* Google Map */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-[#0B1F3F] mb-8 text-center">Find Us on Map</h2>
            <div className="rounded-lg overflow-hidden shadow-xl border border-gray-200">
              <iframe
                src={contactInfo.mapEmbed}
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Tanuku Driving School Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
